'use client';

import AnimatedCard from '@/components/ui/animated-card';
import BorderWrapper from '@/components/ui/border-wrapper';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Image from 'next/image';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { useTranslations } from 'next-intl';
import parse from 'html-react-parser';

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: 'easeInOut', repeat: Infinity },
  },
} as const;

const TeamCulture = () => {
    const t = useTranslations("teamCulture");
    const sectionRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -60px 0px" });

    const teamCulture = [
        {
          title: t("globalTeam") + ' <br />' + t("localCraft"),
          altText: t("globalTeam") + ' ' + t("localCraft"),
          description: t("description"),
          image: '/assets/illustrations/flyingcode-shield.svg',
        },
        {
          title: t("howWeWork"),
          altText: t("howWeWork"),
          description: t("howWeWorkDescription"),
          image: '/assets/illustrations/flyingcode-lightning.svg',
        },
        {
          title: t("globalStandards"),
          altText: t("globalStandards"),
          description: t("globalStandardsDescription"),
          image: '/assets/illustrations/flyingcode-tech.svg',
        },
    ]
  return (
    <section className="section pt-[30px] lg:pt-[50px] pb-[50px]">
      <motion.div
        ref={sectionRef}
        className="container"
        initial="hidden"
        animate={isInView ? "show" : "hidden"}
        variants={{ hidden: { opacity: 1 }, show: { opacity: 1, transition: { staggerChildren: 0.12 } } }}
      >
        <motion.div
          className="section-header text-center mb-[30px] lg:mb-[60px] flex flex-col items-center gap-[20px]"
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } } }}
        >
          <h2 className="text-[28px] md:text-[40px] lg:text-[60px] font-semibold leading-[1.5]">
          {t("sectionTitle")}
          </h2>
          <p className='text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[310px] lg:max-w-[560px]'>{t("description")}</p>
        </motion.div>

        {/* MOBILE + TABLET CAROUSEL */}
        <motion.div
          className="lg:hidden"
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
        >
          <Carousel
            aria-label={t("sectionTitle")}
            opts={{
              align: "center",
              loop: false,
            }}
            className="w-full [&>div]:!overflow-visible"
          >
            <CarouselContent className="-ml-2 md:-ml-[32px]">
              { teamCulture.map((item, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-[32px] basis-[85%] md:basis-3/4">
                    <AnimatedCard className='rounded-[25px] h-full overflow-hidden' circleClassName='w-[100px] h-[100px]'>
                      <BorderWrapper
                          wrapperClass='rounded-[25px] h-full'
                          className='rounded-[25px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] px-[30px] lg:px-[45px] py-[60px] flex flex-col gap-[16px] text-center items-center relative overflow-hidden'
                      >
                          <div
                            className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                            />
                          { item.image && (
                             <Image src={item.image} alt={item.altText} width={86} height={111} className='mb-[15px] lg:mb-[35px] max-w-[70px] lg:max-w-[86px] w-auto h-auto mix-blend-plus-lighter' />
                          )}
                          
                          { item.title && (
                            <h3 className="text-[20px] lg:text-[24px] font-semibold leading-[1.5]">{parse(item.title)}</h3>
                          )}
                          { item.description && (
                            <div className='text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90'>{parse(item.description)}</div>
                          )}
                      </BorderWrapper>  
                    </AnimatedCard>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            {/* MOBILE NAVIGATION */}
            <div className="flex justify-center items-center mt-[30px] gap-[15px]">
              <CarouselPrevious className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
              <CarouselNext className="static translate-x-0 translate-y-0 w-[50px] h-[50px] rounded-full bg-[#4a3290] text-white disabled:bg-[#15092c] cursor-pointer border-white/5 focus:bg-theme focus:text-white" />
            </div>  
          </Carousel>
        </motion.div>

        {/* DESKTOP GRID */}
        <motion.div
          className="hidden lg:grid lg:grid-cols-3 gap-[30px]"
          variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
        >
            { teamCulture.map((item, index) => (
                <div key={index} className="grid__item h-auto">
                <AnimatedCard className='rounded-[25px] h-full overflow-hidden' circleClassName='w-[100px] h-[100px]'>
                <BorderWrapper
                    wrapperClass='rounded-[25px] h-full'
                    className='rounded-[25px] h-full bg-gradient-to-b from-[#121124] to-[#0e0821] px-[45px] py-[60px] flex flex-col gap-[16px] text-center items-center relative overflow-hidden'
                >
                    <div
                      className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/10 rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                      />
                    {
                        item.image && (
                        <Image src={item.image} alt={item.altText} width={86} height={111} className='mb-[35px] w-auto h-auto mix-blend-plus-lighter' />
                        )
                    }
                    
                    { item.title && (
                        <h3 className="text-[24px] font-semibold leading-[1.5]">{parse(item.title)}</h3>
                    )}
                    { item.description && (
                        <div className='text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90'>{parse(item.description)}</div>
                    )}
                </BorderWrapper>  
                </AnimatedCard>
            </div>
            ))}
        </motion.div>
      </motion.div>
    </section>
  )
}

export default TeamCulture